/**
 * EventBus — Typed event emitter for the design editor.
 */
import type { DesignEventMap } from './types';

type Handler<T = unknown> = (payload: T) => void;

class EventBus {
  private listeners = new Map<string, Set<Handler>>();

  /**
   * Subscribe to an event.
   */
  on<K extends keyof DesignEventMap>(
    event: K & string,
    handler: Handler<DesignEventMap[K]>,
  ): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler as Handler);
  }

  /**
   * Unsubscribe from an event.
   */
  off<K extends keyof DesignEventMap>(
    event: K & string,
    handler: Handler<DesignEventMap[K]>,
  ): void {
    this.listeners.get(event)?.delete(handler as Handler);
  }

  /**
   * Subscribe to an event, auto-remove after first call.
   */
  once<K extends keyof DesignEventMap>(
    event: K & string,
    handler: Handler<DesignEventMap[K]>,
  ): void {
    const wrapper: Handler<DesignEventMap[K]> = (payload) => {
      this.off(event, wrapper);
      handler(payload);
    };
    this.on(event, wrapper);
  }

  /**
   * Emit an event. Each listener is isolated: one that throws (often an add-on)
   * is logged and skipped, so it cannot abort the caller, e.g. add-to-cart.
   */
  emit<K extends keyof DesignEventMap>(
    event: K & string,
    payload?: DesignEventMap[K],
  ): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      for (const handler of handlers) {
        try {
          handler(payload);
        } catch (error) {
          console.error(`[Veyo] "${event}" listener failed:`, error);
        }
      }
    }
  }

  /**
   * Remove all listeners for an event, or all listeners entirely.
   */
  clear(event?: string): void {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }
}

/** Singleton event bus instance */
export const eventBus = new EventBus();
export type { EventBus };
